import { Avatar, Button, Chip, Dialog } from '@mui/material'
import moment from 'moment-timezone'
import { useSnackbar } from 'notistack'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { useLoading } from 'providers/context/Loading'
import { KLAVIYO_EVENTS } from 'utils/constants/Enums'
import { GENERAL_ERROR_MESSAGE } from 'utils/constants/Messages'
import { serviceIDs } from 'utils/constants/services'
import { triggerKlaviyoEvent } from 'utils/helper-functions/klaviyoEvents'
import {
  getActiveMembershipDetails,
  getMyMembershipVouchers,
} from 'utils/helper-functions/membershipInfo'
import useNavigate from 'utils/navigation/useCustomNavigate'
import { TIMESLOT_SELECTION_FAILED } from 'utils/sentry/sentry-messages'
import { captureError } from 'utils/sentry/sentry-utils'
import { OptionsTypes } from 'utils/types/enhancementTypes'
import { AppointmentDateTimeSelectData } from 'utils/types/ga4eventsTypes'
import { KlaviyoDataPayload } from 'utils/types/klaviyoPayloadTypes'
import { AppContext } from '../../../providers/context/App'
import { EstheticianListContext } from '../../../providers/context/EstheticianList'
import {
  FindATimeDateChangeAnalytics,
  triggerAppointmentDateTimeSelectEvent,
} from '../../../services/Analytics'
import {
  addBookableItemToCart,
  getCart,
  reserveTimeslotToCart,
} from '../../../services/Cart'
import {
  APPOINTMENT_CONFIRMATION_ROUTE,
  CUSTOMER_SESSION_TOKEN,
  GROUP_BOOKING,
  HEALTH_CHECK_ROUTE,
  HYDRAFACIAL_IDS,
  LASER_HEALTH_CHECK_ROUTE,
} from '../../../utils/constants/Helpers'
import {
  generateAbandonUrl,
  generateAvatarText,
  getLocalstorageItem,
  getOpeningDate,
} from '../../../utils/helper-functions'
import {
  Esthetician,
  EstheticianTimeslotsProps,
  TimeSlot,
} from '../../../utils/types/estheticianTypes'

import { ReactComponent as HIcon } from '../../../assets/icons/H-new.svg'
import { ReactComponent as Card } from '../../../assets/icons/new-card.svg'
import { ReactComponent as NewIcon } from '../../../assets/icons/new.svg'
import { ReactComponent as EmptyState } from '../../../assets/images/booking-emptyState.svg'
import { ReactComponent as TwoMonthAfter } from '../../../assets/images/booking-twoMonth.svg'

import './estheticianTimeslots.scss'

interface LimitEnhancementDialogProps {
  isEnhancementLimitModalOpen: boolean
  setIsEnhancementLimitModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  continueWithoutBoost: (boostName: string) => void
}

export function LimitEnhancementDialog({
  isEnhancementLimitModalOpen,
  setIsEnhancementLimitModalOpen,
  continueWithoutBoost,
}: Readonly<LimitEnhancementDialogProps>) {
  return (
    <Dialog
      className="limit-enhancement-dialog"
      open={isEnhancementLimitModalOpen}
      onClose={() => {
        setIsEnhancementLimitModalOpen(false)
      }}
    >
      <h4>Are you sure?</h4>
      <p>
        The Platinum Party Boost won't be available on the date selected. If you
        want the Boost, please select a day on or before December 31st.
      </p>
      <Button
        fullWidth
        variant="contained"
        disableElevation
        className="app-btn change-date"
        onClick={() => {
          setIsEnhancementLimitModalOpen(false)
        }}
      >
        Change Date
      </Button>
      <Button
        fullWidth
        variant="contained"
        disableElevation
        className="app-btn app-btn-outlined"
        onClick={() => {
          continueWithoutBoost('Platinum Party')
        }}
      >
        Continue Without Boost
      </Button>
    </Dialog>
  )
}

const EstheticianTimeslots = (props: EstheticianTimeslotsProps) => {
  const { estheticianMasterData } = useContext(EstheticianListContext)
  const { updateData, appMasterData } = useContext(AppContext)
  const { pathname, search } = window.location
  const [searchParams] = useSearchParams()
  const { navigate } = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useLoading()
  const isDevEnv = process.env.REACT_APP_ENV === 'development'

  const [estheticians, setEstheticians] = useState<Esthetician[]>([])
  const [monthValidate, setMonthValidate] = useState(false)
  const [isGhostBooking, setIsGhostBooking] = useState(false)
  const [dateLimit, setDateLimit] = useState(2)
  const [preOpeningDate, setPreOpeningDate] = useState<Date>()
  const [isEnhancementLimitModalOpen, setIsEnhancementLimitModalOpen] =
    useState(false)
  const [timeEstheticianSelection, setTimeEstheticianSelection] = useState<{
    selectedTime: TimeSlot
    esthetician: Esthetician
  }>()

  const isLaserFacial = appMasterData.isLaserFacial
  const isMicroFacial = appMasterData.isMicroNeedling

  const proceedNextScreen = async (selectedTime: TimeSlot) => {
    /**
     * - If appointment is within 7 days of booking time
     *   then proceed to Health Check screen
     *   else Appointment detail screen
     *
     */

    const appointmentDate = moment(
      new Date(selectedTime.startTime),
      'DD.MM.YYYY'
    )
    const bookingDate = moment(new Date(), 'DD.MM.YYYY')
    const differenceDays = appointmentDate.diff(bookingDate, 'days')
    const selectedService = appMasterData.category.categoryId

    let baseRoute =
      (selectedService === serviceIDs?.[0] && differenceDays < 6) ||
      HYDRAFACIAL_IDS.includes(selectedService) ||
      isMicroFacial ||
      selectedService === serviceIDs?.[6]
        ? HEALTH_CHECK_ROUTE
        : APPOINTMENT_CONFIRMATION_ROUTE

    let targetRoute = baseRoute

    if (isLaserFacial) {
      targetRoute = LASER_HEALTH_CHECK_ROUTE
    }

    const ga4Data: AppointmentDateTimeSelectData = {
      timeslot_selected: moment
        .tz(selectedTime.startTime, appMasterData.location.tz)
        .format('H:mm'),
      date_selected: moment(appMasterData.selectedDate).format('M-DD-YYYY'),
      location_name: appMasterData.location.name,
      userId: appMasterData.userInfo.boulevard_id ?? '',
      page_location: `${window.location.origin}${targetRoute}`,
      page_referrer: window.location.href,
    }
    triggerAppointmentDateTimeSelectEvent(ga4Data)

    const isUserLogin = Boolean(getLocalstorageItem(CUSTOMER_SESSION_TOKEN))
    if (isUserLogin) {
      const enhancementName = appMasterData.selectedEnhancements.map(
        (a) => a.name
      )
      const enhancementIds = (
        appMasterData.selectedEnhancements as OptionsTypes[]
      ).map((a) => (isDevEnv ? a.boulevard_sandbox_id : a.boulevard_id))
      const selectedDateTime = selectedTime.startTime

      const payloadData: KlaviyoDataPayload = {
        clientId: appMasterData.userInfo.boulevard_id,
        email: appMasterData.userInfo.email,
        location: appMasterData.location.name,
        serviceId: appMasterData.category.categoryId,
        serviceName: appMasterData.category.categoryObj?.name,
        enhancementName,
        enhancementIds,
        selectedDateTime: selectedDateTime,
        abandonLink: generateAbandonUrl(
          KLAVIYO_EVENTS.DATETIME_ST_SELECTED,
          appMasterData,
          moment.parseZone(selectedDateTime).utc(true).format()
        ),
      }
      await triggerKlaviyoEvent(
        KLAVIYO_EVENTS.DATETIME_ST_SELECTED,
        payloadData
      )
    }

    if (searchParams.get('location')) {
      navigate(targetRoute, {
        replace: true,
        state: { from: `${pathname}${search}` },
      })
    } else {
      navigate(targetRoute)
    }
  }

  const onSelectTime = async (
    selectedTime: TimeSlot,
    esthetician: Esthetician
  ) => {
    // START   -------------------------------------------------------------- Check enhancement time limit with selected date
    setTimeEstheticianSelection({
      selectedTime,
      esthetician,
    })
    const startDate = moment(new Date(selectedTime.startTime), 'DD.MM.YYYY')
    const platinumParty = (
      appMasterData.selectedEnhancements as OptionsTypes[]
    ).find((i) => i.name === 'Platinum Party')
    const limitDate = moment(new Date('2023-12-31'), 'DD.MM.YYYY')
    const differenceDays = limitDate.diff(startDate, 'days')
    // Enhancement valid through 31st Dec 2023 only
    if (platinumParty && differenceDays < 0) {
      setIsEnhancementLimitModalOpen(true)
      return
    }
    // END     -------------------------------------------------------------- Check enhancement time limit with selected date
    setLoading(true)
    try {
      const addItemToCart = await addBookableItemToCart(
        appMasterData.cart.cartId,
        appMasterData.category.categoryId,
        esthetician.id === 'any' ? '' : esthetician.id
      )

      if (
        addItemToCart.addCartSelectedBookableItem?.cart.selectedItems.length &&
        appMasterData.selectedEnhancements.length
      ) {
        await Promise.all(
          (appMasterData.selectedEnhancements as OptionsTypes[]).map(
            async (item) => {
              const res = await addBookableItemToCart(
                appMasterData.cart.cartId,
                isDevEnv ? item.boulevard_sandbox_id : item.boulevard_id
              )
              return res
            }
          )
        )
      }

      const getCartValues = await getCart(
        appMasterData.cart.cartId,
        appMasterData.category.categoryId
      )
      let membershipVouchers
      let userActiveMembership
      // Fetch membership vouchers only for logged in users
      if (appMasterData.user.token) {
        userActiveMembership = await getActiveMembershipDetails()
        membershipVouchers = getMyMembershipVouchers(userActiveMembership)
      }

      ;(appMasterData.selectedEnhancements as OptionsTypes[]).forEach(
        (item) => {
          const cartItem = getCartValues.cart.selectedItems.find(
            (obj) =>
              obj.item.id ===
              (isDevEnv ? item.boulevard_sandbox_id : item.boulevard_id)
          )
          item.cartItemId = cartItem?.id
          return item
        }
      )

      const selectedEnhancements = (
        appMasterData.selectedEnhancements as OptionsTypes[]
      ).filter((item) => item.hasOwnProperty('cartItemId'))

      updateData({
        ...appMasterData,
        selectedEnhancements,
        membershipVouchers: membershipVouchers ?? [],
        userActiveMembership: userActiveMembership,
        selectedTime: selectedTime,
        selectedEsthetician: esthetician,
        cart: {
          ...appMasterData.cart,
          summary: getCartValues?.cart?.summary ?? appMasterData.cart.summary,
        },
      })
      FindATimeDateChangeAnalytics(selectedTime.startTime)

      await reserveTimeslotToCart(appMasterData.cart.cartId, selectedTime.id)

      proceedNextScreen(selectedTime)
    } catch (error) {
      captureError(TIMESLOT_SELECTION_FAILED, {
        location: appMasterData.location,
        selectedTime,
        esthetician,
        error,
        functionName: 'onSelectTime',
      })
      enqueueSnackbar(GENERAL_ERROR_MESSAGE, {
        variant: 'error',
      })
    }
    setLoading(false)
  }

  useEffect(() => {
    if (estheticianMasterData.filteredEstheticians.length > 0) {
      setEstheticians(estheticianMasterData.filteredEstheticians)
    } else {
      setEstheticians(estheticianMasterData.defaultEsthetician)
    }
  }, [estheticianMasterData])

  useEffect(() => {
    validateMonth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appMasterData.selectedDate])

  useEffect(() => {
    if (appMasterData.location.enableGhostBooking) {
      setIsGhostBooking(true)
    }

    setDateLimit(
      appMasterData.location.advanceBookingLimit > 0
        ? appMasterData.location.advanceBookingLimit
        : 2
    )

    const openDate = getOpeningDate(appMasterData.location)
    if (openDate) {
      setPreOpeningDate(openDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateMonth = () => {
    const currentData = preOpeningDate ?? new Date()
    const selectedDate = appMasterData.selectedDate
    const difference =
      (selectedDate.getDate() - currentData.getDate()) / 30 +
      selectedDate.getMonth() -
      currentData.getMonth() +
      12 * (selectedDate.getFullYear() - currentData.getFullYear())
    if (difference <= dateLimit) {
      setMonthValidate(false)
    } else {
      setMonthValidate(true)
    }
  }

  let isEmpty =
    Object.values(estheticianMasterData.estheticianTimeslots).flat().length ===
      0 && estheticians.every((a) => !a?.isAny)
  const initialFinished =
    Object.keys(estheticianMasterData.estheticianTimeslots).length > 0

  // If 'any' timeslots exist, prefer that for emptiness check
  if (estheticianMasterData.estheticianTimeslots?.any) {
    isEmpty = estheticianMasterData.estheticianTimeslots.any.length === 0
  }

  // Defer calling onFinish to after render to avoid setState-in-render React error
  const hasAnyTimeslots =
    Boolean(estheticianMasterData.estheticianTimeslots?.any) &&
    estheticianMasterData.estheticianTimeslots.any.length > 0
  const finishCalledRef = useRef(false)
  useEffect(() => {
    if (hasAnyTimeslots && !finishCalledRef.current) {
      finishCalledRef.current = true
      props.onFinish()
    }
    // Only trigger when availability of `any` timeslots changes or onFinish reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnyTimeslots, props.onFinish])

  const navigateToGroup = () => {
    navigate(GROUP_BOOKING)
  }

  /**
   *
   * @param boostName Enhancement to be remove
   *
   * Remove enhancement from context and process further
   */
  const continueWithoutBoost = (boostName: string) => {
    const indexAt = appMasterData.selectedEnhancements.findIndex(
      (i) => i.name === boostName
    )
    if (indexAt >= 0) {
      const prevSelectedEnhancements =
        appMasterData.selectedEnhancements as OptionsTypes[]
      prevSelectedEnhancements.splice(indexAt, 1)
      updateData({
        ...appMasterData,
        selectedEnhancements: [...prevSelectedEnhancements],
      })
      if (
        timeEstheticianSelection?.selectedTime &&
        timeEstheticianSelection?.esthetician
      ) {
        setLoading(true)
        setIsEnhancementLimitModalOpen(false)
        setTimeout(() => {
          onSelectTime(
            timeEstheticianSelection?.selectedTime,
            timeEstheticianSelection?.esthetician
          )
        }, 2000)
      }
    }
  }

  return (
    <>
      {!isEmpty &&
        !monthValidate &&
        estheticians.map((data, id) => {
          // Hides the last seen and time slots, If Dermaplaning selected.
          if (!props.showFilter && !data.isAny) return null
          return (
            <React.Fragment key={data.id}>
              <div
                className={`estheticianlist ${estheticians.length === 1 ? 'border-top' : ''}`}
              >
                {data.isAny ? (
                  <Chip
                    className="any-chip"
                    avatar={
                      <Avatar>
                        <HIcon />
                      </Avatar>
                    }
                    label="Any Esthetician"
                  />
                ) : (
                  !isGhostBooking && (
                    <div className="estheticianlist-header">
                      <Avatar>
                        {generateAvatarText(
                          data.staff.firstName,
                          data.staff.lastName
                        )}
                      </Avatar>
                      <div className="list-profile">
                        <span className="list-profile-name">
                          {data.staff.firstName} {data.staff.lastName.charAt(0)}
                          .
                        </span>
                        {data.lastSeen && (
                          <span className="list-profile-status">
                            Last seen{' '}
                            {moment(data.lastSeen).format('MM/DD/YYYY')}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                )}

                <div className="estheticianlist-timeslots">
                  {!!estheticianMasterData.estheticianTimeslots[data.id] &&
                  estheticianMasterData.estheticianTimeslots[data.id].length >
                    0 ? (
                    estheticianMasterData.estheticianTimeslots[data.id].map(
                      (timeslot) => {
                        return (
                          <div
                            key={timeslot.id}
                            className="timeslots-container"
                            onClick={() => {
                              onSelectTime(timeslot, data).catch((e) =>
                                console.error(e)
                              )
                            }}
                            onKeyDown={() => {
                              onSelectTime(timeslot, data).catch((e) =>
                                console.error(e)
                              )
                            }}
                            role="none"
                          >
                            {moment
                              .tz(timeslot.startTime, appMasterData.location.tz)
                              .format('h:mm A')}
                          </div>
                        )
                      }
                    )
                  ) : (
                    <div className="no-slot">
                      Sorry, no available appointments today
                    </div>
                  )}
                </div>
              </div>
              {(estheticians.length === 1 || id === 1) &&
              appMasterData.location.enableGroupBooking ? (
                <div className="new-booking-card-wrapper">
                  <NewIcon className="new-icon" />
                  <div className="txt">
                    <span className="body-1 bold">Spread the glow. </span>
                    <span className="body-1">
                      Book with a friend (or two!) for a group facial.{' '}
                      <span
                        className="link link-txt"
                        onClick={navigateToGroup}
                        onKeyDown={navigateToGroup}
                        role="none"
                      >
                        Learn More →
                      </span>
                    </span>
                  </div>
                  <div className="card-image">
                    <Card />
                  </div>
                </div>
              ) : (
                <></>
              )}
            </React.Fragment>
          )
        })}

      {!isEmpty &&
        !monthValidate &&
        props.showFilter &&
        !searchParams.get('isRebook') && (
          <div className="estheticianlist-footer">
            <p>Looking for someone in particular?</p>
            <span
              className="link"
              onClick={props.openFilter}
              onKeyDown={props.openFilter}
              role="none"
            >
              Filter by Esthetician
            </span>
          </div>
        )}

      {isEmpty && initialFinished && !monthValidate && (
        <div className="no-results">
          <EmptyState />
          <h3>
            We're all
            <br />
            booked up!
          </h3>
          <p>Try a different day.</p>
        </div>
      )}

      {monthValidate && !searchParams.get('isRebook') && (
        <div className="no-results-month">
          <TwoMonthAfter />
          <h3>
            We can't see
            <br />
            that far ahead
          </h3>
          <div className="parent-wrapper">
            <p>We only allow bookings {dateLimit} months in advance.</p>
            <p>Please try a different date.</p>
          </div>
        </div>
      )}

      {searchParams.get('isRebook') && !isEmpty && (
        <div className="rebook-message">
          You are booking {appMasterData.selectedFacial} with{' '}
          {appMasterData.selectedEsthetician.staff.firstName}{' '}
          {appMasterData.selectedEsthetician.staff.lastName.charAt(0)}. at{' '}
          {appMasterData.location.name}. To choose something else, click the
          back arrow.
        </div>
      )}

      <LimitEnhancementDialog
        isEnhancementLimitModalOpen={isEnhancementLimitModalOpen}
        setIsEnhancementLimitModalOpen={setIsEnhancementLimitModalOpen}
        continueWithoutBoost={continueWithoutBoost}
      />
    </>
  )
}

export default React.memo(EstheticianTimeslots)
